import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subject, of } from 'rxjs';
import { mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';

import { environment } from '../../../../../../environments/environment';
import { Account } from '../../../../../auth/models/account.model';
import { User } from '../../../../../auth/models/user.model';
import { LoginService } from '../../../../../auth/services/login.service';
import { MessengerComponent } from '../../../../../chat/components/messenger/messenger.component';
import { Company } from '../../../../../models/company.model';
import { CompanyService } from '../../../../../services/company.service';
import { ComponentCommService } from '../../../../../services/component-comm.service';
import { DataDogLoggerService } from '../../../../../services/data-dog-logger.service';
import { MaxQuantityConfirmComponent } from '../../../../components/max-quantity-confirm/max-quantity-confirm.component';
import { Negotiation } from '../../models/negotiation.model';
import { Order } from '../../models/order.model';
import { ODD_BUSINESS_TYPE, SiloBag } from '../../models/silo-bag.model';
import { MemoryStorageService } from '../../services/memory-storage.service';
import { NegotiationService } from '../../services/negotiation.service';
import { OrderService } from '../../services/order.service';
import { ProposalDetailComponent } from '../proposal-detail/proposal-detail.component';

@Component({
  selector: 'app-counter-order',
  templateUrl: './counter-order.component.html',
  styleUrls: ['./counter-order.component.scss']
})
export class CounterOrderComponent implements OnInit, OnDestroy {

  @ViewChild('proposalDetail', { static: true }) private readonly proposalDetail: ProposalDetailComponent;
  @ViewChild('unavailableModal', { static: true }) private readonly unavailableModal: TemplateRef<any>;
  @ViewChild('messengerComponent') public readonly messengerComponent: MessengerComponent;

  private account: Account;
  /** True si la company logueada es la de la negociación (en ODD, el productor). Público: lo usa el template. */
  public isOwner: boolean;
  private destroy$ = new Subject<void>();
  private watchingNegotiationId: number | null = null;

  public attachmentAccept: string = "";
  public canEdit: boolean = false;
  public chambers: Location[] = [];
  public company: Company;
  public contract_ids: string;
  public contractsData: any[];
  /** The language currently used. */
  public currentLang: string;
  public editorAccount: boolean;
  public environment = environment;
  public filesToAttach;
  public formMode: boolean = false;
  public hasTurn: boolean = false;
  public hideRelated: boolean;
  public incoming: boolean = false;
  public isActive: boolean = false;
  public isAmendment: boolean = false;
  public isCancellation: boolean = false;
  public isFirst: boolean = false;
  public isNew: boolean = false;
  public isRepresented: boolean;
  public modalRef: BsModalRef;
  public negotiation: Negotiation = new Negotiation();
  public outgoing: boolean = false;
  public refreshTrackRecord = new EventEmitter();
  public relatedNegotiations: Negotiation[];
  public sending: boolean = false;
  /** Clave de traducción del mensaje del spinner mientras `sending` (ej. "Anotando..."). */
  public sendingMessage: string;
  public selectedSiloBags: SiloBag[] = [];
  public siloBagCoverageValid: boolean = false;
  /** SiloIDs de un borrador recuperado desde la vista de la orden (ver AGY-3806). */
  public draftSiloBagIds: string[] = [];
  /** Valor en edición del check "silobolsas negociables" (sólo lo cambia el productor; ver `siloBagsNegotiable`). */
  public siloBagsNegotiableDraft: boolean = true;
  /**
   * El productor puede elegir el check "silobolsas negociables": sólo la primera vez que
   * compromete bolsas (negociación nueva, o una privada que el comprador creó sin bolsas).
   * Se calcula al cargar porque `attachSiloBagsSnapshot()` pisa `negotiation.silo_bags` antes de enviar.
   */
  public canSetSiloBagsNegotiable: boolean = false;
  public tableData: {
    [key: string]: any
  } = {};
  public user: User;

  constructor(
    private companyService: CompanyService,
    private componentComm: ComponentCommService,
    private memoryStorageService: MemoryStorageService,
    private modalService: BsModalService,
    private orderService: OrderService,
    private negotiationService: NegotiationService,
    private route: ActivatedRoute,
    private router: Router,
    private loginService: LoginService,
    private translateService: TranslateService,
    private dataDogLoggerService: DataDogLoggerService
  ) { }

  ngOnInit(): void {
    this.currentLang = this.translateService.currentLang === 'es' ? undefined : this.translateService.currentLang;

    this.companyService.watchAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(account => {
        if (account) {
          this.account = account;
          this.editorAccount = this.account.is.commercial || this.account.is.operations;
        }
      });

    this.loginService.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });

    this.orderService.getExtraAttributes<Location>('camara_arbitral')
      .pipe(takeUntil(this.destroy$))
      .subscribe(chambers => {
        this.chambers = chambers;
      });

    this.companyService.watch().pipe(
      tap(company => {
        this.company = company;
      }),
      switchMap(company => {
        return this.route.data.pipe(
          tap((data: { order: Order, negotiation: Negotiation }) => {
            if (data.negotiation) {
              this.negotiation = data.negotiation;
              if (this.negotiation.contracts.length) {
                this.contract_ids = this.negotiation.contracts.map(c => c.id).join(",");
              }
              this.componentComm.emit({ name: 'app-title', title: this.negotiation.order.product.name + ' (' + this.translateService.instant('GLOBAL.COUNTERORDER') + ' #' + this.negotiation.id + ')' });
            } else if (data.order) {
              this.negotiation.order = data.order;
              this.negotiation.proposal = this.orderService.cloneProposal(data.order);
              this.negotiation.company = this.company;
              this.componentComm.emit({ name: 'app-title', title: this.negotiation.order.product.name + ' (' + this.translateService.instant('COUNTERORDER_VIEW.NEW') + ')' });
              this.restoreSiloBagsDraft(data.order.id);
            }
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.checkStatus();
    });
  }

  private loadRelated(): void {
    if (this.negotiation.status.id === 7 &&
      this.negotiation.linked_negotiations &&
      this.negotiation.linked_negotiations.length) {
      this.negotiationService.watchLinked(this.company.id, this.negotiation.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(negotiations => {
          if (!negotiations) return;

          this.relatedNegotiations = negotiations;
        });
    }
  }

  private watchNegotiation(): void {
    if (this.negotiation.id && this.watchingNegotiationId !== this.negotiation.id) {
      this.watchingNegotiationId = this.negotiation.id;
      this.negotiationService.watch(this.company.id, this.negotiation.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((negotiation) => {
          if (!negotiation) return;

          this.parseNegotiation(negotiation);
        });
    }
  }

  private parseNegotiation(negotiation: Negotiation): void {
    this.negotiation = negotiation;
    this.contract_ids = this.negotiation.contracts.map(c => c.id).join(",");
    this.proposalDetail.form.form.markAsPristine();
    this.checkStatus();
    this.refreshTrackRecord.emit();
  }

  reload(): void {
    this.refreshTrackRecord.emit();
  }

  private resetVariables(): void {
    this.canEdit = false;
    this.formMode = false;
    this.hasTurn = false;
    this.incoming = false;
    this.isActive = false;
    this.isAmendment = false;
    this.isFirst = false;
    this.isNew = false;
    this.outgoing = false;
    this.isCancellation = false;
    this.canSetSiloBagsNegotiable = false;
  }

  private checkStatus(): void {
    this.resetVariables();

    // La compañia puede no estar lista todavía, esperar el próximo check.
    if (this.company) {
      const status: number = this.negotiation.status.id;

      this.isAmendment = (this.negotiation.times_booked > 0 && ![7, 20, 21, 22].includes(status)) || //
        (this.route.snapshot.data && this.route.snapshot.data.isAmendment && status === 7); // New amendment

      this.isOwner = this.company.id === this.negotiation.company.id;
      this.isRepresented = !this.isOwner && (this.company.id !== this.negotiation.order.company.id);

      this.isNew = !this.negotiation.id || // There is no neegotiation ID
        (status === 7 && this.isAmendment); // It's a new Amendment

      this.isActive = [1, 2, 3, 12, 13, 20, 21].includes(status);

      this.formMode = (this.isNew || this.isActive) && !this.isRepresented;

      this.incoming =
        ([1, 3, 12, 20].includes(status) && !this.isOwner) ||
        ([2, 13, 21].includes(status) && this.isOwner);

      this.outgoing =
        ([1, 3, 12, 20].includes(status) && this.isOwner) ||
        ([2, 13, 21].includes(status) && !this.isOwner);

      this.isFirst = this.negotiation.previous_proposal === null;

      this.hasTurn = (this.isNew || (this.incoming && !this.negotiation.order.on_hold)) && !this.isRepresented;

      // Pre-anote (status 3) editable sólo en ODD: el comprador puede contraofertar, no sólo Anotar/Rechazar.
      // Acotado a ODD porque `pre_book` existe en otros mercados y cambiaría su flujo (AGY-3822).
      this.canEdit = !this.isRepresented &&
        (this.isNew ||
          (this.hasTurn &&
            (status === 1 || status === 2 || status === 13 ||
              (status === 3 && this.isOddOrder))
          ));

      this.isCancellation = [20, 21].includes(status);
      this.siloBagsNegotiableDraft = this.siloBagsNegotiable;
      this.canSetSiloBagsNegotiable = this.isOwner && (!this.negotiation.id || !this.hasCommittedSiloBags);
      this.loadRelated();

      this.watchNegotiation();
    }
  }

  public getNegotiableMode(): 'hidden' | 'editable' | 'visible' {
    if (this.company && this.negotiation) {
      // Possible values: hidden, editable, visible

      if (this.isAmendment) return 'hidden';
      if (this.canEdit && this.company.id === this.negotiation.order.company.id) return 'editable';
      if (this.isActive || this.isNew) return 'visible';

      return 'hidden';
    }
  }

  public send(): void {
    this.attachSiloBagsSnapshot();
    this.sendingMessage = this.isAmendment ? 'COUNTERORDER_VIEW.PROCESSING.AMENDMENT' : 'COUNTERORDER_VIEW.PROCESSING.SEND';

    this.confirmMaxQuantity()
      .pipe(
        tap(() => this.sending = true),
        switchMap(value =>
          this.orderService.counterOrder(this.negotiation, this.company.id).pipe(
            mergeMap(negotiation => {
              if (!this.isNew) {
                return this.negotiationService.get(this.company.id, negotiation.id);
              } else {
                return of(negotiation);
              }
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: negotiation => {
          if (this.isNew) {
            // Redirect the user to the created counterorder
            this.router.navigate(['/company', this.company.id, 'counter-order', negotiation.id]);
          } else {
            this.sending = false;
            this.parseNegotiation(negotiation);
          }
        },
        error: error => {
          this.handleActionError(error);
        }
      });
  }

  /** True cuando la orden parent es de tipo de negocio ODD (silobolsas). */
  public get isOddOrder(): boolean {
    // El `business_type` ya NO se negocia: proposal-detail lo muestra fijo, así que la proposal
    // es un clon de la order y ambas coinciden. Se conserva la prioridad proposal→order por las
    // negociaciones y canjes anteriores a ese cambio —donde la orden type 7 puede no
    // conservarlo mientras la proposal sí— y por paridad con el backend
    // (BartersController::isOddBarterOrder).
    const type = this.negotiation?.proposal?.business_detail?.business_type
      ?? this.negotiation?.order?.business_detail?.business_type;
    return type === ODD_BUSINESS_TYPE;
  }

  /** True cuando ya hay silobolsas comprometidas (vienen del backend, pivote vigente). */
  public get hasCommittedSiloBags(): boolean {
    return !!this.negotiation?.silo_bags?.length;
  }

  /** Valor vigente (backend) del check del productor. Ausente = negociable (negociaciones previas al flag). */
  public get siloBagsNegotiable(): boolean {
    return this.negotiation?.silo_bags_negotiable !== false;
  }

  /** El productor marcó las bolsas como no negociables y la company logueada no es el productor. */
  public get siloBagsLockedForMe(): boolean {
    return !this.isOwner && !this.siloBagsNegotiable;
  }

  /**
   * Silobolsas como campo negociable más: editable por quien tiene el turno, tenga o no ya
   * comprometidas. Si el productor las bloqueó, la contraparte cae en `showCommittedSiloBags`
   * (snapshot readonly, sin pedir el stock del productor) y reenvía las mismas bolsas.
   */
  public get canEditSiloBags(): boolean {
    return this.isOddOrder && this.formMode && this.canEdit && !this.siloBagsLockedForMe;
  }

  /**
   * Adjunta el snapshot de silobolsas antes de enviar. Se escribe en `negotiation.silo_bags`
   * (raíz, retrocompat) y en `negotiation.proposal.silo_bags` (nuevo).
   */
  private attachSiloBagsSnapshot(): void {
    if (!this.isOddOrder) return;
    const refs = this.canEditSiloBags
      ? this.selectedSiloBags.map(b => b.toRef())
      : (this.negotiation.silo_bags ?? []);
    this.negotiation.silo_bags = refs;
    if (this.negotiation.proposal) {
      this.negotiation.proposal.silo_bags = refs;
    }
    // Sólo el productor, la primera vez que compromete bolsas, decide si son negociables; después el backend lo ignora.
    if (this.canSetSiloBagsNegotiable && this.canEditSiloBags) {
      this.negotiation.silo_bags_negotiable = this.siloBagsNegotiableDraft;
    }
  }

  /** Selector editable: es el turno de esta company para editar la propuesta. */
  public get showSiloBagSelector(): boolean {
    return this.canEditSiloBags;
  }

  /** Vista de solo lectura: hay bolsas comprometidas y no es el turno de editarlas. */
  public get showCommittedSiloBags(): boolean {
    return this.isOddOrder && !this.canEditSiloBags && this.hasCommittedSiloBags;
  }

  /** True cuando el dueño de la orden ve las bolsas que comprometió la contraparte. */
  public get siloBagsCommittedByCounterparty(): boolean {
    return this.hasCommittedSiloBags &&
      this.negotiation?.company?.id !== this.company?.id;
  }

  /** SiloIDs vigentes (pivote): preselección del selector al entrar en modo edición. */
  public get committedSiloBagIds(): string[] {
    return (this.negotiation?.silo_bags ?? []).map(bag => bag.silobag_id);
  }

  /**
   * True si la selección difiere del pivote vigente. Las silobolsas viven fuera de
   * `proposalDetail.form`, así que sin esto Enviar no se habilita al cambiar sólo bolsas.
   */
  public get siloBagsDirty(): boolean {
    if (!this.canEditSiloBags) return false;

    // Cambiar sólo el check del productor (primer compromiso de bolsas) también es un cambio enviable.
    if (this.canSetSiloBagsNegotiable && this.siloBagsNegotiableDraft !== this.siloBagsNegotiable) return true;

    const committed = this.committedSiloBagIds;
    const selected = this.selectedSiloBags.map(bag => bag.silo_id);

    if (committed.length !== selected.length) return true;
    return selected.some(id => committed.indexOf(id) === -1);
  }

  public onSiloBagsChange(bags: SiloBag[]): void {
    this.selectedSiloBags = bags;
  }

  public onSiloBagsValidity(valid: boolean): void {
    this.siloBagCoverageValid = valid;
  }

  public onSiloBagsNegotiableChange(negotiable: boolean): void {
    this.siloBagsNegotiableDraft = negotiable;
  }

  /** Recupera el borrador guardado por ViewOrderComponent.goToCounterOrder(), si es de esta orden. */
  private restoreSiloBagsDraft(orderId: number): void {
    const draft = this.memoryStorageService.data.siloBagsDraft;
    if (draft?.orderId === orderId) {
      this.draftSiloBagIds = draft.siloIds ?? [];
      // La negociación todavía no existe: se deja en el modelo para que checkStatus()
      // inicialice el check con lo que el productor eligió en la vista de la orden.
      this.negotiation.silo_bags_negotiable = draft.negotiable !== false;
    }
    delete this.memoryStorageService.data.siloBagsDraft;
  }

  public accept(): void {
    if ([20, 21].includes(this.negotiation.status.id)) {
      // es una cancelacion
      this.sendingMessage = 'COUNTERORDER_VIEW.PROCESSING.ACCEPT';
      this.sending = true;
      this.negotiationService.acceptCancelation(this.negotiation, this.company.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          this.sending = false;
        });

    } else if (this.negotiation.status.id === 12) {
      // es un nego request
      this.sendingMessage = 'COUNTERORDER_VIEW.PROCESSING.ACCEPT';
      this.sending = true;
      this.negotiationService.acceptRequest(this.negotiation, this.company.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          this.sending = false;
          // FAS-2899
          // this.router.navigate(['/company', this.company.id, 'my-operations'], {
          //   queryParams: {
          //     status_group_orders: 1,
          //     order_by: '-updated_at'
          //   }
          // });
        });

    } else {
      // es una orden
      this.attachSiloBagsSnapshot();
      this.sendingMessage = this.isAmendment ? 'COUNTERORDER_VIEW.PROCESSING.ACCEPT' : 'COUNTERORDER_VIEW.PROCESSING.BOOK';

      this.confirmMaxQuantity()
        .pipe(
          tap(() => this.sending = true),
          switchMap(value =>
            this.negotiationService.accept(this.negotiation, this.company.id)
          ),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: response => {
            // `sending` queda en true hasta que la navegación destruya el componente,
            // así el spinner no desaparece antes de que cargue la pantalla destino.
            this.router.navigate(['/company', this.company.id, 'company-traded-orders'], {
              queryParams: {
                order_by: '-original_booking_date',
                'filters[settled]': false
              }
            });
          },
          error: error => {
            this.handleActionError(error);
          }
        });
    }
  }

  public reject(): void {
    this.sendingMessage = this.isNew ? 'GLOBAL.PROCESSING' :
      (this.hasTurn ? 'COUNTERORDER_VIEW.PROCESSING.REJECT' : 'COUNTERORDER_VIEW.PROCESSING.WITHDRAW');
    this.sending = true;

    if (this.isNew) {
      // ODD: preservar la selección en curso al volver a la orden, para que
      // ViewOrderComponent la recupere (mismo mecanismo que goToCounterOrder, en sentido
      // inverso — ver AGY-3806).
      if (this.isOddOrder && this.selectedSiloBags.length) {
        this.memoryStorageService.data.siloBagsDraft = {
          orderId: this.negotiation.order.id,
          siloIds: this.selectedSiloBags.map(bag => bag.silo_id),
          negotiable: this.siloBagsNegotiableDraft
        };
      }
      this.router.navigateByUrl('/company/' + this.company.id + '/order/' + this.negotiation.order.id);
    }
    else {
      if (this.negotiation.status.id === 20 ||
        this.negotiation.status.id === 21) {
        // es un retiro de la cancelacion o un rechazo de cancelacion
        this.negotiationService.accept(this.negotiation, this.company.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(response => {
            this.sending = false;
          });
      }
      else if (this.negotiation.status.id === 12) {
        // es un nego request
        this.negotiationService.rejectRequest(this.negotiation, this.company.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(response => {
            this.sending = false;
          });

      } else {
        // es una orden
        this.negotiationService.reject(this.negotiation, this.company.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: response => {
              // `sending` queda en true hasta que la navegación destruya el componente.
              if (this.isAmendment) {
                this.router.navigate(['/company', this.company.id, 'company-traded-orders'], {
                  queryParams: {
                    order_by: '-original_booking_date',
                    'filters[settled]': false
                  }
                });
              } else {
                this.router.navigate(['/company', this.company.id, 'my-operations'], {
                  queryParams: {
                    order_by: '-updated_at',
                    status_group_orders: 1
                  }
                });
              }
            },
            error: error => {
              this.handleActionError(error);
            }
          });
      }
    }
  }

  private confirmMaxQuantity(): Observable<boolean> {
    if (
      this.negotiation.order.business_detail.quantity.type.id === 2 &&
      this.negotiation.order.company.id === this.company.id &&
      this.negotiation.order.totalBooked + this.negotiation.proposal.business_detail.quantity.value > this.negotiation.order.business_detail.quantity.value
    ) {
      this.modalRef = this.modalService.show(MaxQuantityConfirmComponent, {
        initialState: {
          negotiation: this.negotiation
        }
      });

      return this.modalRef.content.onConfirm;
    } else {
      return of(true);
    }
  }

  private handleActionError(error: any): void {
    this.dataDogLoggerService.warn(error.message, error.error);

    this.checkStatus();
    this.sending = false;

    if (
      error instanceof HttpErrorResponse &&
      error.status === 403
    ) {
      this.modalRef = this.modalService.show(this.unavailableModal, {
        ignoreBackdropClick: true
      });
    } else {
      throw error;
    }
  }

  // Amendments
  public cancelAmendment(): void {
    this.router.navigateByUrl('/company/' + this.company.id + '/counter-order/' + this.negotiation.id);
  }
  public rejectAmendment(): void {
    this.reject();
  }
  public sendAmendment(): void {
    this.send();
  }
  public acceptAmendment(): void {
    this.accept();
  }

  // External data
  public tableLoaded(e, key: string): void {
    if (e.data.length) this.tableData[key] = e;
    else this.tableData[key] = undefined;
  }

  public contractsLoaded(e): void {
    this.contractsData = e;
  }

  public changeFile(event): void {
    // Triggered by input component
    this.filesToAttach = event.target.files;
  }

  public unlink(negotiation: Negotiation): void {
    this.sendingMessage = 'GLOBAL.PROCESSING';
    this.sending = true;
    this.negotiationService.unlink(this.company.id, this.negotiation.id, [negotiation.id])
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.sending = false;
      });
  }

  public showSlotsRequested: boolean = false;
  public toggleSlotsRequested(): void {
    this.showSlotsRequested = !this.showSlotsRequested;
  }

  /** @ignore */
  ngOnDestroy(): void {
    // Complete all subscriptions using takeUntil pattern
    // This includes: companyService.watch, loginService.getCurrentUser,
    // Pusher WebSocket subscription (negotiationService.watch),
    // and all HTTP subscriptions (accept, reject, send, unlink operations)
    this.destroy$.next();
    this.destroy$.complete();
  }
}
